import type { ComputedRef, Ref } from 'vue'
import { storeToRefs } from 'pinia'
import type {
    DesignStudioWeddingRingInput,
    ProductAnalyticsFragment,
    RingSizeInput,
    WeddingRingFragment,
} from '@/graphql/api'
import {} from '@/graphql/api'
import { getCommissionJunctionInput } from '@/helpers/commission-junction'
import { useCustomerRegistrationInputStore } from '@/composables/useCustomerRegistrationInput'
import {
    createResponseAndPath,
    type ErrorCollectionWithTraces,
    useLastResponseErrorAtPath,
} from '@/helpers/errors'
import { getUri } from '@/helpers/getCurrentUri'
import { useHandleCta } from '../composables/useHandleCta'
import { useHandleAddToCartSuccess } from '../composables/useHandleAddToCartSuccess'
import { useIsAdmin } from '@/components/app/isAdmin'
import { useCreateDesignStudioProductAndAddToCartWeddingRingExistingCustomerMutation } from '@/graphql/api/cart/wedding-ring/mutations/createDesignStudioProductAndAddToCartWeddingRingExistingCustomer.mutation.generated.ts'
import { useCreateDesignStudioProductAndAddToCartWeddingRingNewCustomerMutation } from '@/graphql/api/cart/wedding-ring/mutations/createDesignStudioProductAndAddToCartWeddingRingNewCustomer.mutation.generated.ts'
import { logUnexpectedError } from '@/helpers'

interface WeddingRingAddToCart {
    isEnquiryModalOpen: Ref<boolean>
    closeEnquiryModal(): void
    submitNewCustomer(successCallback: () => void): void
    fetchingNewCustomer: Ref<boolean>
    fetchingExistingCustomer: Ref<boolean>
    errors: ComputedRef<ErrorCollectionWithTraces>
    handleAddToCart(): void
}

export function useWeddingRingAddToCart(
    ring: Readonly<
        Ref<(WeddingRingFragment & ProductAnalyticsFragment) | null | undefined>
    >,
    handleRingSizeNotSelected: () => RingSizeInput | null,
    engraving: Readonly<Ref<string>>
): WeddingRingAddToCart {
    const commissionJunction = getCommissionJunctionInput()
    const getInput = (): DesignStudioWeddingRingInput | null => {
        const uri = getUri()

        const ringSize = handleRingSizeNotSelected()

        if (!ring.value || !uri) {
            logUnexpectedError('Could not get input', {
                ring: ring.value,
                uri,
            })

            return null
        }

        if (!ringSize) {
            return null
        }

        return {
            catalogueWeddingRingCode: ring.value.catalogueCode,
            ringSize,
            uri,
            engraving: engraving.value,
            commissionJunction,
        }
    }

    const { prepare } = useHandleAddToCartSuccess(ring)

    // Existing customer
    const existingCustomerResponse =
        useCreateDesignStudioProductAndAddToCartWeddingRingExistingCustomerMutation()
    const {
        executeMutation: mutationExistingCustomer,
        fetching: fetchingExistingCustomer,
    } = existingCustomerResponse

    const submitExistingCustomer = () => {
        const input = getInput()

        if (!input) {
            return
        }

        const handleAddToCartSuccess = prepare()

        mutationExistingCustomer({
            input,
        }).then(({ error }) => {
            handleAddToCartSuccess({
                error,
                data: {
                    customer: undefined,
                },
            })
        })
    }

    // New customer
    const newCustomerResponse =
        useCreateDesignStudioProductAndAddToCartWeddingRingNewCustomerMutation()
    const {
        executeMutation: mutationNewCustomer,
        fetching: fetchingNewCustomer,
    } = newCustomerResponse

    const { customerRegistrationInput } = storeToRefs(
        useCustomerRegistrationInputStore()
    )

    const isAdmin = useIsAdmin()
    const submitNewCustomer = (successCallback: () => void) => {
        const input = getInput()
        const customer = customerRegistrationInput.value

        if (!input || !customer) {
            return
        }

        const handleAddToCartSuccess = prepare()

        mutationNewCustomer({
            input,
            customer,
            isConsultant: isAdmin.value,
        }).then(({ error, data: response }) => {
            const data =
                response?.createDesignStudioProductAndAddToCartWeddingRingNewCustomer

            handleAddToCartSuccess({
                error,
                data,
            })

            if (data?.customer?.login) {
                successCallback()
            }
        })
    }

    const { isEnquiryModalOpen, closeEnquiryModal, handleAddToCart } =
        useHandleCta(submitExistingCustomer, handleRingSizeNotSelected)

    const errors = useLastResponseErrorAtPath([
        createResponseAndPath(
            existingCustomerResponse,
            'createDesignStudioProductAndAddToCartWeddingRingExistingCustomer'
        ),
        createResponseAndPath(
            newCustomerResponse,
            'createDesignStudioProductAndAddToCartWeddingRingNewCustomer'
        ),
    ])

    return {
        isEnquiryModalOpen,
        closeEnquiryModal,
        submitNewCustomer,
        fetchingNewCustomer,
        fetchingExistingCustomer,
        errors,
        handleAddToCart,
    }
}
